<template>
    <select
        ref="select"
        v-bind="$attrs"
        :multiple="multiple"
        :disabled="disabled"
        :required="required"
    >
        <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
        <option v-for="option in mappedOptions" :key="option.key" :value="option.key">{{ option.label }}</option>
    </select>
</template>
<script>
import TomSelect from "tom-select";
import { emitModel } from "./model";
export default {
    name: "SbSelect",
    emits: ["update:modelValue", "change", "search"],
    inheritAttrs: false,
    props: {
        modelValue: { default: undefined },
        options: { type: Array, default: () => [] },
        label: { type: String, default: "label" },
        reduce: { type: Function, default: (option) => option },
        multiple: { type: Boolean, default: false },
        placeholder: { type: String, default: null },
        disabled: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        filterable: { type: Boolean, default: true },
        optionHtml: { type: Function, default: null },
    },
    data() { return { control: null }; },
    computed: {
        currentValue() { return this.modelValue; },
        mappedOptions() {
            return this.options.map((option, index) => ({
                key: String(index),
                option,
                label: typeof option === "object" ? option[this.label] : option,
                reduced: this.reduce(option),
                html: this.optionHtml?.(option) ?? null,
            }));
        },
    },
    watch: {
        currentValue: { deep: true, handler() { this.syncValue(); } },
        options: { deep: true, handler() { this.refreshOptions(); } },
        disabled(value) {
            if (!this.control) { return; }
            value ? this.control.disable() : this.control.enable();
        },
    },
    mounted() {
        this.control = new TomSelect(this.$refs.select, {
            placeholder: this.placeholder,
            plugins: this.multiple ? ["remove_button"] : [],
            searchField: this.filterable ? ["text"] : [],
            render: {
                option: (data, escape) => `<div>${data.html ?? escape(data.text)}</div>`,
            },
            onChange: (value) => this.onChange(value),
            onType: (query) => this.$emit("search", query),
        });
        this.refreshOptions();
        this.syncValue();
    },
    beforeUnmount() { this.control?.destroy(); },
    methods: {
        keyForValue(value) {
            return this.mappedOptions.find((option) => option.reduced === value)?.key;
        },
        syncValue() {
            if (!this.control) { return; }
            const value = this.multiple
                ? (this.currentValue || []).map((item) => this.keyForValue(item)).filter((item) => item !== undefined)
                : (this.keyForValue(this.currentValue) ?? "");
            this.control.setValue(value, true);
        },
        refreshOptions() {
            if (!this.control) { return; }
            this.control.clearOptions();
            this.mappedOptions.forEach((option) => this.control.addOption({ value: option.key, text: option.label, html: option.html }));
            this.control.refreshOptions(false);
            this.syncValue();
        },
        onChange(value) {
            const keys = Array.isArray(value) ? value : (this.multiple ? String(value).split(",").filter(Boolean) : [value]);
            const values = keys.map((key) => this.mappedOptions.find((option) => option.key === String(key))?.reduced).filter((item) => item !== undefined);
            const resolved = this.multiple ? values : (values[0] ?? null);
            emitModel(this, resolved);
            this.$emit("change", resolved);
        },
    },
};
</script>
